import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { useLazyQuery } from '@apollo/client';
import {
  DuplicateStatus,
  GetDuplicateStatusQuery,
  GetDuplicateStatusQueryVariables,
} from '@/gql/graphql';
import { GET_DUPLICATE_STATUS } from '@/queries';
import { useEffect, useState } from 'react';
import LinearLoader from '@/components/LinearLoader';
import { MAX_POLLING_DURATION } from '@/constants';

interface ConfirmationProps {
  fromDuplicateApp?: boolean;
  applicationId?: string;
}
const Confirmation = ({ fromDuplicateApp = false, applicationId }: ConfirmationProps) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [dupHeader, setDupHeader] = useState('Thank you!');
  const [dupSub1, setDupSub1] = useState(
    "Your request is currently under review. Contact your site's SiteVault administrator for immediate access to SiteVault."
  );
  const [shouldPoll, setShouldPoll] = useState(fromDuplicateApp);
  const [getDuplicateStatusLoading, setGetDuplicateStatusLoading] = useState(fromDuplicateApp);

  const [getDuplicateStatus] = useLazyQuery<
    GetDuplicateStatusQuery,
    GetDuplicateStatusQueryVariables
  >(GET_DUPLICATE_STATUS, {
    fetchPolicy: 'network-only',
  });

  let timerId;

  const handleGetDupStatus = async () => {
    await getDuplicateStatus({
      variables: {
        id: applicationId,
      },
      onCompleted: (res) => {
        if (res?.getDuplicateStatus) {
          if (res.getDuplicateStatus === DuplicateStatus.None) {
            return;
          }
          if (timerId) {
            clearInterval(timerId);
          }
          setGetDuplicateStatusLoading(false);
          switch (res?.getDuplicateStatus) {
            // Based on duplicateStatus render different copies
            case DuplicateStatus.ExistingUser:
              setDupHeader('Great news! You have a SiteVault account.');
              setDupSub1(
                `Your email address is already associated with an account for your site. Check your email for further instructions.`
              );
              return;
            case DuplicateStatus.DomainMatch:
              setDupHeader('Great news! Your site already uses SiteVault.');
              setDupSub1(
                '1. Check your email for a list of SiteVault administrators at your site\n' +
                  '2. Contact one of your SiteVault administrators to request access'
              );
              return;
            case DuplicateStatus.DomainMismatch:
              setDupHeader('Great news! Your site already uses SiteVault.');
              setDupSub1(
                `1. We’ve sent your contact information to your site’s SiteVault administrator\n` +
                  `2. Contact your site's SiteVault administrator for immediate access to SiteVault`
              );
              return;
            default:
              setDupHeader('Thank you!');
              setDupSub1(
                `Your request is currently under review. Contact your site's SiteVault administrator for immediate access to SiteVault.`
              );
          }
        } else if (timerId) {
          clearInterval(timerId);
        }
      },
    });
  };

  useEffect(() => {
    if (fromDuplicateApp && applicationId && shouldPoll) {
      setGetDuplicateStatusLoading(true);
      timerId = setInterval(async () => {
        await handleGetDupStatus();
      }, 1000);
      return () => clearInterval(timerId);
    }
    if (!shouldPoll) {
      setGetDuplicateStatusLoading(false);
    }
    if (timerId) {
      return () => clearInterval(timerId);
    }
    return () => {};
  }, [fromDuplicateApp, shouldPoll]);

  useEffect(() => {
    // Stop polling after 5s
    if (shouldPoll) {
      setTimeout(() => {
        setShouldPoll(false);
      }, MAX_POLLING_DURATION);
    }
  }, [shouldPoll]);

  if (getDuplicateStatusLoading) {
    return <LinearLoader />;
  }

  return (
    <Box>
      <Box
        textAlign="center"
        mt={mdUp ? 9 : 4}
        mb={7}
        sx={{ color: (theme) => theme.palette.grey[800] }}>
        {fromDuplicateApp ? (
          <>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              {dupHeader}
            </Typography>
            <Typography
              variant="h5"
              sx={{ marginTop: (theme) => theme.spacing(1), whiteSpace: 'pre-wrap' }}
              color="inherit">
              {dupSub1}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              Thank you!
            </Typography>
            <Typography
              variant="h4"
              sx={{ marginTop: (theme) => theme.spacing(1) }}
              color="inherit">
              Your account request is currently being reviewed for approval.
            </Typography>
          </>
        )}
      </Box>

      <Box
        mt={9}
        mb={3}
        p={3}
        sx={{
          borderRadius: 1,
          border: (theme) => `2px solid ${theme.palette.grey[200]}`,
          color: (theme) => theme.palette.grey[800],
        }}>
        <Typography variant="body1" gutterBottom color="inherit" py={3}>
          Ensuring receipt of Veeva emails is critical as they will include important information
          about your account. Please{' '}
          <Link
            target="_blank"
            href={process.env.NEXT_PUBLIC_ADDRESS_BOOK_GUIDE_EXTERNAL_URL}
            sx={(theme) => {
              return {
                color: theme.palette.primary.main,
                textDecorationColor: theme.palette.primary.main,
                '&:hover': {
                  color: theme.palette.primary.main,
                  opacity: 0.8,
                },
              };
            }}>
            add Veeva to your address book
          </Link>
          .
        </Typography>

        <Typography variant="body1" gutterBottom color="inherit" pb={1}>
          Next steps:
        </Typography>

        <ul style={{ paddingLeft: '24px' }}>
          <li style={{ paddingBottom: '16px' }}>
            <Link
              target="_blank"
              href={process.env.NEXT_PUBLIC_HELP_CENTER_EXTERNAL_URL}
              sx={(theme) => {
                return {
                  color: theme.palette.primary.main,
                  textDecorationColor: theme.palette.primary.main,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                  },
                };
              }}>
              Visit the Help Center
            </Link>{' '}
            to access training and self-service resources
          </li>
          <li>
            <Link
              target="_blank"
              href={process.env.NEXT_PUBLIC_SITEVAULT_YOUTUBE_EXTERNAL_URL}
              sx={(theme) => {
                return {
                  color: theme.palette.primary.main,
                  textDecorationColor: theme.palette.primary.main,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                  },
                };
              }}>
              Check out our YouTube channel
            </Link>{' '}
            for bite-sized tutorials
          </li>
        </ul>
      </Box>
    </Box>
  );
};

export default Confirmation;
